import styles from './JsonViewer.module.css';

interface JsonValueProps {
	value: unknown;
	depth: number;
}

function JsonValue({ value, depth }: JsonValueProps) {
	if (value === null) {
		return <span className={styles.null}>null</span>;
	}
	if (typeof value === 'boolean') {
		return <span className={styles.boolean}>{value ? 'true' : 'false'}</span>;
	}
	if (typeof value === 'number') {
		return <span className={styles.number}>{value}</span>;
	}
	if (typeof value === 'string') {
		return <span className={styles.string}>"{value}"</span>;
	}
	if (Array.isArray(value)) {
		if (value.length === 0) {
			return <span className={styles.punctuation}>[]</span>;
		}
		return (
			<details open={depth === 0} className={styles.collapsible}>
				<summary className={styles.summary}>
					<span className={styles.punctuation}>[</span>
					<span className={styles.collapsedHint}>{value.length} items</span>
					<span className={styles.punctuation}>]</span>
				</summary>
				<div className={styles.nested}>
					{value.map((item, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: array indices are stable keys for display-only list
						<div key={i} className={styles.row}>
							<span className={styles.key}>{i}</span>
							<span className={styles.colon}>: </span>
							<JsonValue value={item} depth={depth + 1} />
						</div>
					))}
				</div>
			</details>
		);
	}
	if (typeof value === 'object') {
		const obj = value as Record<string, unknown>;
		const keys = Object.keys(obj);
		if (keys.length === 0) {
			return <span className={styles.punctuation}>{'{}'}</span>;
		}
		return (
			<details open={depth === 0} className={styles.collapsible}>
				<summary className={styles.summary}>
					<span className={styles.punctuation}>{'{'}</span>
					<span className={styles.collapsedHint}>{keys.length} keys</span>
					<span className={styles.punctuation}>{'}'}</span>
				</summary>
				<div className={styles.nested}>
					{keys.map((k) => (
						<div key={k} className={styles.row}>
							<span className={styles.key}>{k}</span>
							<span className={styles.colon}>: </span>
							<JsonValue value={obj[k]} depth={depth + 1} />
						</div>
					))}
				</div>
			</details>
		);
	}
	return <span className={styles.string}>{String(value)}</span>;
}

interface JsonViewerProps {
	data: Record<string, unknown>;
}

export function JsonViewer({ data }: JsonViewerProps) {
	const keys = Object.keys(data);
	if (keys.length === 0) {
		return <p className={styles.empty}>No data fields.</p>;
	}
	return (
		<div className={styles.viewer}>
			{keys.map((k) => (
				<div key={k} className={styles.row}>
					<span className={styles.key}>{k}</span>
					<span className={styles.colon}>: </span>
					<JsonValue value={data[k]} depth={0} />
				</div>
			))}
		</div>
	);
}
